import type {Band} from './breakpoints';

/** Store state selects the header variant (ruling 5). */
export type StoreVariant = 'ecom' | 'nonEcom';

/**
 * Header heights from Figma (spec § 7.2, redline non-ecom). Ecommerce full: mobile 152
 * (strap 40 + nav 52 + search 60), desktop-class 112 (top 56 + nav 56). Non-ecommerce full
 * and compact (both variants): mobile 92, desktop-class 56.
 *
 * SHORTCUT: layout literals with no token. Upgrade path: DIS-1735 layout tokens.
 */
const HEIGHTS = {
  mobile: {ecom: {full: 152, compact: 92}, nonEcom: {full: 92, compact: 92}},
  desktopClass: {ecom: {full: 112, compact: 56}, nonEcom: {full: 56, compact: 56}},
} as const;

export const heightsFor = (band: Band, variant: StoreVariant): {full: number; compact: number} => {
  const {full, compact} = HEIGHTS[band === 'mobile' ? 'mobile' : 'desktopClass'][variant];
  return {full, compact};
};

/**
 * RP1, RP2: compact at `scrollY >=` the full height while scrolling down; full again after
 * the compact height of upward travel, or when `scrollY <` the full height.
 */
export const compactThresholds = (
  band: Band,
  variant: StoreVariant,
): {compactAt: number; expandAfterUp: number} => {
  const {full, compact} = heightsFor(band, variant);
  return {compactAt: full, expandAfterUp: compact};
};

export type CompactTracker = {compact: boolean; lastY: number; upTravel: number};

export const INITIAL_TRACKER: CompactTracker = {compact: false, lastY: 0, upTravel: 0};

/**
 * One scroll sample through the compact state machine. Pure and worklet-safe: Task A8 runs
 * it inside `useAnimatedReaction`, so it reads only its arguments.
 */
export const nextCompactTracker = (
  prev: CompactTracker,
  y: number,
  t: {compactAt: number; expandAfterUp: number},
): CompactTracker => {
  'worklet';
  const clampedY = y < 0 ? 0 : y;
  if (clampedY < t.compactAt) {
    return {compact: false, lastY: clampedY, upTravel: 0};
  }
  if (clampedY > prev.lastY) {
    return {compact: true, lastY: clampedY, upTravel: 0};
  }
  if (clampedY < prev.lastY) {
    const upTravel = prev.upTravel + (prev.lastY - clampedY);
    return {
      compact: upTravel >= t.expandAfterUp ? false : prev.compact,
      lastY: clampedY,
      upTravel,
    };
  }
  return prev;
};
